import FullCalendar from '@fullcalendar/react'
import dayGridPlugin from '@fullcalendar/daygrid'
import interactionPlugin from "@fullcalendar/interaction"
import {useState, useMemo, useEffect, useRef} from "react";
import {usePeople, useReunions} from "../hooks/hooks_index";
import {Loading, MediaImage, getLocalDateString} from "../components/components_index";
import {Link} from 'react-router-dom';
import '../styles/Calendar.css';

export default function Calendar() {
  const {data: people, isLoading: peopleLoading, error: peopleError} = usePeople();
  const {data: reunions, isLoading: reunionsLoading, error: reunionsError} = useReunions();
  const [peopleBirthdays, setPeopleBirthdays] = useState([]);
  const [peopleAnnivs, setPeopleAnnivs] = useState([]);
  const [reunionDates, setReunionDates] = useState([]);
  const [visibleYear, setVisibleYear] = useState(new Date().getFullYear());
  const [visibleMonth, setVisibleMonth] = useState(new Date().getMonth());
  const [selectedEvent, setSelectedEvent] = useState(null);
    
  const calendarRef = useRef(null);

  const MONTHS = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];
  
  const YEARS = Array.from(
    { length: new Date().getFullYear() + 5 - 1900 + 1 },
    (_, i) => 1900 + i
  );

  const peopleList = useMemo(() => (people ? Object.entries(people) : []), [people]);
  const reunionList = useMemo(() => (reunions ? Object.entries(reunions) : []), [reunions]);

  useEffect(() => {
    if (!peopleList.length) return;

    const birthdays = peopleList
      .filter(([id, p]) => p.b_details?.b_day && !id.startsWith("P0"))
      .map(([id, p]) => {
        const name = p.AKA || `${p.f_name} ${p.l_name}`;
        const { b_year, b_month, b_day } = p.b_details;
        return {
          id,
          name,
          birthday: `${b_year}-${String(b_month).padStart(2,'0')}-${String(b_day).padStart(2,'0')}`
        };
      });

    setPeopleBirthdays(birthdays);
  }, [peopleList]);

  useEffect(() => {
    if (!peopleList) return;

    function fetchAnnivs() {
      const annivs = peopleList
        .filter(([id]) => id.endsWith("0") && !id.startsWith("P0"))
        .filter(([_, person]) => person.m_details?.length)
        .map(([id, person]) => {
          const lastMarriage = person.m_details[person.m_details.length - 1];

          if (lastMarriage.div_year) return null;

          const { m_year, m_month, m_day, spouse } = lastMarriage;
          const name = person.AKA?.split(' ')[0] || person.f_name;
          // const name = person.f_name;

          return {
            id,
            name,
            anniv: `${m_year}-${String(m_month).padStart(2, '0')}-${String(m_day).padStart(2, '0')}`,
            spouseID: spouse
          };
        })
        .filter(Boolean);

      setPeopleAnnivs(annivs);
    }

    fetchAnnivs();
  }, [peopleList]);

  useEffect(() => {
    if (!reunionList.length) return;

    const reunionDays = reunionList
      .filter(([id, r]) => r.start_date && r.end_date)
      .map(([id, r]) => {
        const year = r.year;
        const {s_month, s_day} = r.start_date;
        const {e_month, e_day} = r.end_date;
        const r_num = r.reunion_num ?? "No Num";
        return {
          id,
          year,
          s_date: `${year}-${String(s_month).padStart(2,'0')}-${String(s_day).padStart(2,'0')}`,
          e_date: `${year}-${String(e_month).padStart(2,'0')}-${String(e_day).padStart(2,'0')}`,
          r_num
        };
      });

    setReunionDates(reunionDays);
  }, [reunionList]);
  
  const calendarEvents = useMemo(() => {
    const events = [];

    for (const person of peopleBirthdays) {
      const [year, month, day] = person.birthday.split('-').map(Number);
      const age = visibleYear - year;
      if (age < 0) continue;
      const ageSuffix = (() => {
        if (age >= 11 && age <= 13) {
          return `th`;
        }

        const n = age % 10;
        switch (n) {
          case 1: return "st";
          case 2: return "nd";
          case 3: return "rd";
          default: return `th`;
        }
      })()
      const visibleAge = age+ageSuffix
      let titleString = `${person.name}'s ${visibleAge} Birthday`
      if (age === 0) {
        titleString = `${person.name}'s Birth`
      }

      events.push({
        id: `b-${person.id}-${visibleYear}`,
        title: titleString,
        date: `${visibleYear}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`,
        allDay: true,
        className: 'birthday-event',
        extendedProps: {
          type: "birthday",
          personID: person.id,
          visible: visibleAge,
        }
      });
    }

    for (const person of peopleAnnivs) {
      const [year, month, day] = person.anniv.split('-').map(Number);
      const spouseMatch = peopleList.find(([id]) => id === person.spouseID);
      const length = visibleYear - year;
      if (length < 0) continue;
      const lengthSuffix = (() => {
        const n = length % 10;
        switch (n) {
          case 1: return "st";
          case 2: return "nd";
          case 3: return "rd";
          default: return `th`;
        }
      })()
      const visibleLength = length+lengthSuffix
      let titleString = `${person.name} and ${spouseMatch?(spouseMatch[1].AKA||`${spouseMatch[1].f_name} ${spouseMatch[1].l_name}`):person.spouseID}'s ${visibleLength} Anniversary`
      if (length === 0) {
        titleString = `${person.name} and ${spouseMatch?spouseMatch[1].AKA:person.spouseID}'s Wedding Day`
      }

      events.push({
        id: `a-${person.id}-${visibleYear}`,
        title: titleString,
        date: `${visibleYear}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`,
        allDay: true,
        className: 'anniversary-event',
        extendedProps: {
          type: "anniversary",
          personID: person.id,
          spouseID: person.spouseID,
          visible: visibleLength,
        }
      });
    }

    for (const reunion of reunionDates) {
      const [year, s_month, s_day] = reunion.s_date.split('-').map(Number);
      const [, e_month, e_day] = reunion.e_date.split('-').map(Number);
      const r_num = reunion.r_num

      const startDate = reunion.s_date;
      const endDate = reunion.e_date;
      const todayStr = getLocalDateString();

      const isCurrentReunion = 
        todayStr >= startDate &&
        todayStr <= endDate;

      const numSuffix = (() => {
        const n = r_num % 10;
        switch (n) {
          case 1: return "st";
          case 2: return "nd";
          case 3: return "rd";
          default: return `th`;
        }
      })()
      const visibleNum = r_num+numSuffix

      let titleString = `${year} Reunion`;

      events.push({
        id: reunion.id,
        title: titleString,
        start: `${year}-${String(s_month).padStart(2, '0')}-${String(s_day).padStart(2, '0')}`,
        end: `${year}-${String(e_month).padStart(2, '0')}-${String(e_day+1).padStart(2, '0')}`,
        allDay: true,
        className: `reunion-event ${isCurrentReunion?'reunion-current':''}`,
        extendedProps: {
          type: "reunion",
          reunionID: reunion.id,
          reunion_num: visibleNum
        }
      });
    }

    // const testEvents = [
    //   {id: "hi", title:"birthday", date:`${visibleYear}-08-10`, className:"birthday-event", allDay:true, extendedProps:{type:"birthday",personID:"P000000"}},
      // {id: "hi1", title:"anniversary", date:`${visibleYear}-05-03`, className:"anniversary-event", allDay:true, extendedProps:{type:"anniversary",personID:"P000000",spouseID:"P000001"}},
      // {id: "hi2", title:"Upcoming Reunion", start:`2026-04-27`, end:`2026-05-06`, className:"reunion-event", allDay:true,extendedProps:{type:"reunion",reunionID:"R1900"}}
    // ];
    // events.push(...testEvents);

    return events;
  }, [peopleBirthdays, peopleAnnivs, reunionDates, visibleYear, peopleList]);

  if (peopleLoading || reunionsLoading) return <Loading/>;
  if (peopleError || reunionsError) return <div>Error</div>;
  if (!peopleList || !reunionList) return <Loading/>;

  return (
    <div className='calendar-page'>
      <div className="page-title">Family Calendar</div>
      <div className='calendar-container'>
        <div className="calendar-header">
          <div className='select-container'>
            <select
              className="month-select"
              value={visibleMonth}
              onChange={(e) => {
                const month = Number(e.target.value);
                setVisibleMonth(month);

                const calendarApi = calendarRef.current.getApi();
                calendarApi.gotoDate(new Date(visibleYear, month, 1));
              }}
            >
              {MONTHS.map((month, index) => (
                <option key={month} value={index}>
                  {month}
                </option>
              ))}
            </select>
            <select
              className="year-select"
              value={visibleYear}
              onChange={(e) => {
                const year = Number(e.target.value);
                setVisibleYear(year);

                const calendarApi = calendarRef.current.getApi();
                calendarApi.gotoDate(new Date(year, visibleMonth, 1));
              }}
            >
              {YEARS.toReversed().map((year, index) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>
        </div>

        <FullCalendar
          ref={calendarRef}
          plugins={[dayGridPlugin, interactionPlugin]}
          initialView="dayGridMonth"
          events={calendarEvents}
          eventClick={(info) => {
            setSelectedEvent({
              id: info.event.id,
              title: info.event.title,
              start: info.event.start,
              end: info.event.end,
              allDay: info.event.allDay,
              classNames: info.event.classNames,
              ...info.event.extendedProps
            });
          }}
          datesSet={(info) => {
            const current = info.view.currentStart;
            setVisibleYear(current.getFullYear());
            setVisibleMonth(current.getMonth());
          }}
          fixedWeekCount={true}
          expandRows={true}
        />
        {selectedEvent && (
          <EventPopup
            event={selectedEvent}
            onClose={() => setSelectedEvent(null)}
            peopleList={peopleList}
            reunionList={reunionList}
            visibleYear={visibleYear}
          />
        )}
      </div>
    </div>
  )
}

function EventPopup({event, onClose, peopleList, reunionList, visibleYear}) {
  // const id = event.id;
  const title = event.title;
  const start = event.start;
  const end = event.end;
  // const allDay = event.allDay;
  const type = event.type;
  const personID = event.personID;
  const spouseID = event.spouseID;
  const reunionID = event.reunionID;
  // const visible = event.visible;
  const reunion_num = event.reunion_num;
  // const today = new Date();
  const todaySplit = getLocalDateString();
  const startSplit = getLocalDateString(start);

  const monthName = start.toLocaleString("en-US", { month: "long" });
  // const dayName = getDayWithSuffix(start);
  const dayName = start.getDate();
  let endName, endMonth
  if (reunionID) {
    const displayEnd = new Date(end);
    displayEnd.setDate(displayEnd.getDate() - 1);

    endMonth = displayEnd.toLocaleString("en-US", { month: "long" });
    // endName = getDayWithSuffix(displayEnd);
    endName = displayEnd.getDate();
  }

  const person = getPersonByID(peopleList, personID)
  let spouse
  if (spouseID) {
    spouse = getPersonByID(peopleList, spouseID)
  }
  const reunion = getReunionByID(reunionList, reunionID);
  // const 

  // let death
  // let todayTime
  // if (person.d_details) {
  //   death = new Date(`${person.d_details.d_year}-${person.d_details.d_month}-${person.d_details.d_day}`).getTime()
  //   todayTime = new Date(todaySplit).getTime()
  //   console.log(death)
  //   console.log(todayTime)
  // }
  // if (`${person.d_details.d_year}-${person.d_details.d_month}-${person.d_details.d_day}`< toString(todaySplit)) {
  //   console.log(todaySplit)
  //   console.log(`${person.d_details.d_year}-${person.d_details.d_month}-${person.d_details.d_day}`)
  // }

  return (
    <div
      className='event-popup'
      onClick={onClose}
    >
      {type === "birthday" &&
        <div className='popup-box-calendar birthday-box' onClick={e => e.stopPropagation()}>
          <button className='close-popup-calendar' onClick={onClose}>{'\u00D7'}</button>
          <MediaImage className='popup-img-calendar' mediaID={person.profile_pic ?? "S_logo_PNG.png"} alt={title}/>
          <div className='popup-content-calendar'>
            {(person.d_details && person.d_details.d_year < visibleYear) && 
              <div>{(todaySplit === startSplit)?'Today would be':`${monthName} ${dayName}, ${visibleYear} ${(todaySplit > startSplit)?"would have been":"would be"}`}</div>
            }
            {(!person.d_details || person.d_details.d_year >= visibleYear) &&
              <div>{(todaySplit === startSplit)?'Today is':`${monthName} ${dayName}, ${visibleYear} ${(todaySplit < startSplit)?'is':'was'}`}</div>
            }
            <div className='box-title'>{title}!</div>
            <Link to={`/people/${personID}`}>{person.AKA?.split(' ')[0]??`${person.f_name}`}'s Page</Link>
          </div>
        </div>
      }
      {type === "anniversary" &&
        <div className='popup-box-calendar anniversary-box no-anniversary-photo' onClick={e => e.stopPropagation()}>
          <button className='close-popup-calendar' onClick={onClose}>{'\u00D7'}</button>
          <div className='anniversary-images'>
            <MediaImage className='popup-img-calendar' mediaID={person.profile_pic ?? "S_logo_PNG.png"} alt={title}/>
            <MediaImage className='popup-img-calendar' mediaID={spouse?.profile_pic ?? "S_logo_PNG.png"} alt={title}/>
          </div>
          <div className='popup-content-calendar'>
            <div>{(person.d_details || spouse?.d_details) ? `${monthName} ${dayName}, ${visibleYear} ${(todaySplit > startSplit)?"would have been":"would be"}` : `${monthName} ${dayName}, ${visibleYear} ${(todaySplit <= startSplit)?'is':'was'}`}</div>
            <div className='box-title'>{title}!</div>
            <div className='anniversary-links'>
              <Link to={`/people/${personID}`}>{person.AKA?.split(' ')[0]??`${person.f_name}`}'s Page</Link>
              <Link to={`/people/${spouseID}`}>{spouse?.AKA?.split(' ')[0]??`${spouse?.f_name??spouseID}`}'s Page</Link>
            </div>
          </div>
        </div>
      }
      {type === "reunion" &&
        <div className='popup-box-calendar reunion-popup-box' onClick={e => e.stopPropagation()}>
          <button className='close-popup-calendar' onClick={onClose}>{'\u00D7'}</button>
          <MediaImage className='popup-img-calendar reunion-popup-img' mediaID={reunion.main_pic ?? "S_logo_PNG.png"} alt={title}/>
          <div className='popup-content-calendar'>
            <div>{monthName} {dayName} to {(monthName===endMonth)?"":endMonth} {endName}, {visibleYear} {(todaySplit <= startSplit)?'is':'was'}</div>
            <div className='box-title'>The {reunion_num ? `${reunion_num} Spradley` : visibleYear} Reunion!</div>
            <Link to={`/reunion/${reunionID}`}>{reunionID.slice(1,6)} Reunion Page</Link>
          </div>
        </div>
      }
      
    </div>
  )
}

function getPersonByID(peopleList, id) {
  const match = peopleList.find(([personId]) => personId === id);
  return match ? match[1] : null;
}

function getReunionByID(reunionList, id) {
  const match = reunionList.find(([reunionId]) => reunionId === id);
  return match ? match[1] : null;
}
